import React, { FC, useEffect, useRef, useState, useMemo } from "react";

import { SubscriptionAdvantagesList } from "../SubscriptionAdvantagesList/SubscriptionAdvantagesList";

// UI components
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { Box } from "@UI/Components/Box/Box";
import { Tag } from "@UI/Components/Tag/Tag";
import { Collapse } from "@UI/Components/Transition/Transition";
import { BrandPlus } from "@UI/Components/Icons/BrandPlus";
import { ChevronDown } from "@UI/Components/Icons/ChevronDown";
import { ResponsiveFormattedMessage } from "@Shared/Components/ResponsiveFormattedMessage/ResponsiveFormattedMessage";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// hooks
import useMediaQuery from "@UI/hooks/useMediaQuery";
import useI18n from "@I18n/hooks/useI18n";
import { useLocation } from "@Libs/Router/hooks/useLocation";

// types
import {
  RouterLink,
  RouterLinkProps,
} from "@Libs/Router/Components/RouterLink/RouterLink";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { ChooseFormulaPageRedesignSubscriptionFormulaCard } from "./ABTests/ChooseFormulaPageRedesign/ChooseFormulaPageRedesignSubscriptionFormulaCard";

interface Props {
  link: RouterLinkProps;
}

export const SubscriptionFormulaCard: FC<Props> = ({ link }) => {
  const element = useRef<HTMLDivElement>(null);
  const { hash } = useLocation();
  const { isMobile, isDesktop } = useMediaQuery();
  const { formatNumber } = useI18n();
  const hashMatch = useMemo(
    () => hash === "#subscription-formula-card",
    [hash]
  );
  const {
    isEnabled: isChooseFormulaPageRedesignEnabled,
    isLoading: isChooseFormulaPageRedesignLoading,
  } = useFeatureFlag("choose_formula_page_redesign");

  const [showAdvantages, setShowAdvantages] = useState(hashMatch);

  const toggleAdvantages = () => setShowAdvantages(!showAdvantages);

  useEffect(() => {
    if (hashMatch && element.current && isMobile) {
      setTimeout(() => {
        element.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
  }, [hashMatch, element.current, isMobile]);

  if (isChooseFormulaPageRedesignLoading) {
    return <LoadingScreen />;
  }

  if (isChooseFormulaPageRedesignEnabled) {
    return (
      <ChooseFormulaPageRedesignSubscriptionFormulaCard
        link={{
          ...link,
          route: ["SERVICE_OFFERS", "SERVICE_OFFER", "ACCEPTANCE", "SUBSCRIBE"],
        }}
      />
    );
  }

  return (
    <Card
      id="subscription-formula-card"
      data-testid="subscription-formula-card"
      corner
      ref={element}
      header={
        <Box
          display={isMobile ? null : "flex"}
          alignItems="center"
          paddingBottom={[2, 4]}
        >
          <Heading as="h3" marginRight="3">
            <ResponsiveFormattedMessage
              mobile={messages.subscriptionFormula}
              desktop={messages.subscriptionFormulaShort}
              values={{
                plus: () => (
                  <Icon
                    as={BrandPlus}
                    boxSize="5"
                    marginLeft="1"
                    display="inline"
                  />
                ),
              }}
            />
          </Heading>
          <Tag
            rounded="full"
            marginY={[2, 0]}
            colorScheme="seaGreen"
            flexShrink={0}
          >
            <FormattedMessage
              {...messages.xPerMonth}
              values={{
                price: formatNumber(14.99, {
                  style: "currency",
                  currency: "EUR",
                }),
              }}
            />
          </Tag>
        </Box>
      }
      footer={
        <RouterLink
          as={Button}
          colorScheme="seaGreen"
          size="lg"
          width="full"
          data-testid="choose-subscription-formula"
          {...link}
        >
          <FormattedMessage {...messages.choose} />
        </RouterLink>
      }
    >
      <Box marginBottom="6">
        <Text marginBottom="4">
          <FormattedMessage {...messages.zeroServiceFeeOnAllServices} />
        </Text>
        {!isDesktop ? (
          <>
            <Button
              id="subscription-advantages-link"
              variant="link"
              colorScheme="pink"
              onClick={toggleAdvantages}
              paddingX="0"
            >
              <FormattedMessage {...messages.otherBenefits} />
              <Icon
                as={ChevronDown}
                marginLeft="2"
                transform={showAdvantages ? "rotate(180deg)" : undefined}
                transition="transform 0.2s"
              />
            </Button>
            <Collapse in={showAdvantages}>
              <Box paddingY="4">
                <SubscriptionAdvantagesList />
              </Box>
            </Collapse>
          </>
        ) : (
          <SubscriptionAdvantagesList />
        )}
      </Box>
    </Card>
  );
};
